import { format } from 'date-fns';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useAppConfig } from 'state';

import { Enums as CoreEnums, DicomMetadataStore, ServicesManager } from '@core';
import { DEFAULT_DATE_FORMAT } from '@utils';
import { PatientHistoryDTO, PatientHistoryFilter, RESOURCES } from 'types';
import useDebounce from './useDebounce';

export const usePatientHistory = (servicesManager: ServicesManager) => {
	const [appConfig] = useAppConfig();
	const baseURL = appConfig.dataSources[0].configuration.apiRoot;
	const { viewportGridService, cornerstoneViewportService, sessionAuthenticationService, hangingProtocolService } =
		servicesManager.services;

	const [activeViewportID, setActiveViewportID] = useState(viewportGridService.getActiveViewportId());

	/// logic ghi nhớ ca primary, sử dụng session storage 
	// để đề phòng người dùng bấm F5 sau khi mở các ca trước đây có thể gây sai ca primary
	const primaryStudyRef = useRef<string | null>(null);

	const studies = hangingProtocolService.studies;
	if (studies.length > 0) {
		const currentPatientID = studies[0].PatientID;
		const savedPrimaryUID = sessionStorage.getItem('primaryStudyUID');

		if (savedPrimaryUID) {
			const savedStudyMetadata = DicomMetadataStore.getStudy(savedPrimaryUID);

			// Logic này check xem primary study ở tab này có phải của bệnh nhân đang xem hay không
			// Nếu là của bệnh nhân khác thì reset lại primary study
			// Đề phòng cho trường hợp người dùng mở ca khác ở trên cùng tab này
			if (savedStudyMetadata && savedStudyMetadata.PatientID !== currentPatientID) {
				sessionStorage.removeItem('primaryStudyUID');
				primaryStudyRef.current = null;
			} else {
				// Nếu đúng bệnh nhân, nạp vào Ref
				primaryStudyRef.current = savedPrimaryUID;
			}
		}

		// Nếu sau khi kiểm tra mà Ref vẫn trống (hoặc vừa reset xong)
		if (!primaryStudyRef.current) {
			const firstUID = studies[0].StudyInstanceUID;
			primaryStudyRef.current = firstUID;
			sessionStorage.setItem('primaryStudyUID', firstUID);
		}
	}

	const primaryStudyInstanceUID = primaryStudyRef.current;

	useEffect(() => {
		const onActiveViewportIdChanged = ({ viewportId }) => setActiveViewportID(viewportId);
		const onViewportsReady = () => setActiveViewportID(viewportGridService.getActiveViewportId());

		const subscriptions = [
			viewportGridService.subscribe(viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED, onActiveViewportIdChanged),
			viewportGridService.subscribe(viewportGridService.EVENTS.VIEWPORTS_READY, onViewportsReady),
			viewportGridService.subscribe(viewportGridService.EVENTS.GRID_STATE_CHANGED, ({ state }) => {
				if (state.activeViewportId) {
					setActiveViewportID(state.activeViewportId);
				}
			}),
		];

		return () => {
			subscriptions.forEach((sub) => sub.unsubscribe());
		};
	}, [viewportGridService]);

	console.log('Active Viewport ID in usePatientHistory:', activeViewportID);

	const study = useMemo(() => {
		if (!activeViewportID) {
			return null;
		}
		const viewportInfo = cornerstoneViewportService.getViewportInfo(activeViewportID);
		if (!viewportInfo) {
			return null;
		}
		const viewportData = viewportInfo.getViewportData();
		if (!viewportData?.data?.[0]?.StudyInstanceUID) {
			return null;
		}
		if (
			viewportData.viewportType === CoreEnums.ViewportTypes.ORTHOGRAPHIC ||
			viewportData.viewportType === CoreEnums.ViewportTypes.VOLUME_3D
		) {
			return DicomMetadataStore.getStudy(viewportData.data[0].StudyInstanceUID);
		} else {
			return DicomMetadataStore.getStudy(viewportData.data[0].StudyInstanceUID);
		}
	}, [activeViewportID, cornerstoneViewportService]);

	const sessionID = sessionAuthenticationService.getState()?.id;
	const [filter, setFilter] = useState<PatientHistoryFilter>({
		patientID: study?.PatientID || '',
		startDate: format(new Date(), DEFAULT_DATE_FORMAT),
		endDate: format(new Date(), DEFAULT_DATE_FORMAT),
	});

	const [patientHistoryList, setPatientHistoryList] = useState<PatientHistoryDTO[]>([]);
	const debouncedFilter = useDebounce(filter, 300);

	// Cập nhật filter khi study thay đổi
	useEffect(() => {
		if (study?.PatientID && filter.patientID !== study.PatientID) {
			setFilter((prev) => ({
				...prev,
				patientID: study.PatientID,
			}));
		}
	}, [study, filter.patientID]);

	const updateSession = useCallback(
		async (studyInstanceUIDs: string[]) => {
			if (sessionID) {
				try {
					const response = await fetch(`${baseURL}/${RESOURCES.SESSION}`, {
						method: 'PUT',
						headers: {
							'content-type': 'application/json',
						},
						body: JSON.stringify({
							id: sessionID,
							studyInstanceUIDs,
						}),
					});
					return response;
				} catch (error) {
					console.log(error);
				}
			}
		},
		[baseURL, sessionID],
	);
	/**
	 * CALL API TO GET PATIENT HISTORY
	 */
	const getPatientHistory = useCallback(
		async (payload: PatientHistoryFilter): Promise<PatientHistoryDTO[]> => {
			let patientHistories: PatientHistoryDTO[] = [];
			if (sessionID) {
				try {
					await fetch(`${baseURL}/${RESOURCES.STUDY}/search`, {
						method: 'POST',
						headers: {
							'content-type': 'application/json',
						},
						body: JSON.stringify({
							...payload,
							sessionID,
						}),
					})
						.then((response) => response.json())
						.then((data) => {
							patientHistories = data.body;
						});
				} catch (error) {
					console.log(error);
				}
			}
			return patientHistories.map((item) => {
				return { ...item, id: item.studyInstanceUID };
			});
		},
		[baseURL, sessionID],
	);

	useEffect(() => {
		if (!debouncedFilter.patientID) {
			return;
		}
		const setPatientHistories = async () => {
			const data = await getPatientHistory({ ...debouncedFilter });
			setPatientHistoryList(data);
		};
		setPatientHistories();
	}, [debouncedFilter, getPatientHistory]);

	return { updateSession, patientHistoryList, filter, setFilter, study, primaryStudyInstanceUID };
};
